<template>
  <div class="row mt-4" v-if="tieneData">
    <div class="col-12">
      <div class="card ficha-listado">
        <div class="px-4 pb-4 card-body">

          <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
            <div>
              <h6 class="mb-0 panel-section-title">Suscripciones</h6>
              <p class="text-xs opacity-7 mb-0">Estado actual de los suscriptores de pago recurrente</p>
            </div>
            <div class="susc-altas-badge">
              <span class="susc-altas-label">{{ datos.filtro_periodo ? 'Altas en el periodo' : 'Total histórico' }}</span>
              <span class="susc-altas-valor">{{ datos.altas_periodo ?? 0 }}</span>
            </div>
          </div>

          <!-- KPIs (clickables como filtro) -->
          <div class="row g-2 mb-3">
            <div class="col-6 col-md-2">
              <div class="susc-kpi susc-kpi--ok" :class="{ 'susc-kpi--active': filtroEstado === 'activa' }" @click="setFiltro('activa')">
                <div class="susc-kpi__k">Activas</div>
                <div class="susc-kpi__v">{{ datos.activas }}</div>
                <div class="susc-kpi__desc">Pagando o en trial, con acceso</div>
              </div>
            </div>
            <div class="col-6 col-md-2">
              <div class="susc-kpi susc-kpi--warn" :class="{ 'susc-kpi--active': filtroEstado === 'por_vencer' }" @click="setFiltro('por_vencer')">
                <div class="susc-kpi__k">Próx. renovaciones</div>
                <div class="susc-kpi__v">{{ datos.por_vencer_7d }}</div>
                <div class="susc-kpi__desc">Cobro en menos de 7 días</div>
              </div>
            </div>
            <div class="col-6 col-md-2">
              <div class="susc-kpi susc-kpi--gracia" :class="{ 'susc-kpi--active': filtroEstado === 'gracia' }" @click="setFiltro('gracia')">
                <div class="susc-kpi__k">En gracia</div>
                <div class="susc-kpi__v">{{ datos.en_gracia }}</div>
                <div class="susc-kpi__desc">Cobro fallido, aún con acceso</div>
              </div>
            </div>
            <div class="col-6 col-md-2">
              <div class="susc-kpi susc-kpi--err" :class="{ 'susc-kpi--active': filtroEstado === 'vencida' }" @click="setFiltro('vencida')">
                <div class="susc-kpi__k">Vencidas</div>
                <div class="susc-kpi__v">{{ datos.vencidas }}</div>
                <div class="susc-kpi__desc">Gracia expirada, sin acceso</div>
              </div>
            </div>
            <div class="col-6 col-md-2">
              <div class="susc-kpi susc-kpi--muted" :class="{ 'susc-kpi--active': filtroEstado === 'cancelada' }" @click="setFiltro('cancelada')">
                <div class="susc-kpi__k">Canceladas</div>
                <div class="susc-kpi__v">{{ datos.canceladas }}</div>
                <div class="susc-kpi__desc">Canceladas explícitamente</div>
              </div>
            </div>
          </div>

          <!-- Cards dinámicas por suscripción / producto -->
          <div v-if="productosActivos.length > 1" class="susc-productos mb-3">
            <button
              v-for="p in productosActivos"
              :key="p.nombre"
              class="susc-prod-chip"
              :class="{ 'susc-prod-chip--active': filtroProducto === p.nombre }"
              @click="setFiltroProducto(p.nombre)"
            >
              <span class="susc-prod-nombre">{{ p.nombre }}</span>
              <span class="susc-prod-count">{{ p.activas }}</span>
            </button>
          </div>

          <!-- Buscador + filtros activos -->
          <div class="susc-toolbar mb-3">
            <input
              v-model="filtroTexto"
              type="text"
              class="input-buscar"
              placeholder="Buscar por email, referencia, producto, Telegram…"
            />
            <div v-if="filtroProducto" class="susc-filtro-activo">
              <span class="susc-filtro-badge">{{ filtroProducto }}</span>
              <button class="susc-filtro-clear" @click="setFiltroProducto('')">✕</button>
            </div>
            <div v-if="filtroEstado" class="susc-filtro-activo">
              <span class="susc-filtro-badge">
                <strong>{{ labelEstado(filtroEstado === 'por_vencer' ? 'activa' : filtroEstado) }}{{ filtroEstado === 'por_vencer' ? ' (próximas a vencer)' : '' }}</strong>
              </span>
              <button class="susc-filtro-clear" @click="setFiltro('')">✕</button>
            </div>
          </div>

          <!-- Tabla -->
          <div v-if="lista.length" class="table-responsive">
            <table class="table table-flush table-listado table-empirulada">
              <thead class="thead-light">
                <tr>
                  <th @click="ordenar('email')" :class="sortCol === 'email' ? sortDir : ''">Email</th>
                  <th @click="ordenar('producto')" :class="sortCol === 'producto' ? sortDir : ''">Producto</th>
                  <th class="text-center" @click="ordenar('estado')" :class="sortCol === 'estado' ? sortDir : ''">Estado</th>
                  <th class="text-center" @click="ordenar('periodo_inicio')" :class="sortCol === 'periodo_inicio' ? sortDir : ''">Alta</th>
                  <th class="text-center" @click="ordenar('periodo_fin')" :class="sortCol === 'periodo_fin' ? sortDir : ''">Próx. renovación</th>
                  <th class="text-center" @click="ordenar('gracia_hasta')" :class="sortCol === 'gracia_hasta' ? sortDir : ''">Vence acceso</th>
                  <th class="text-center sin-orden">Acciones</th>
                </tr>
              </thead>
              <tbody class="text-sm">
                <tr v-for="s in listaVisible" :key="s.suscripcion_id">
                  <td class="email-cell">{{ s.email }}</td>
                  <td class="text-xs opacity-8">{{ s.producto }}</td>
                  <td class="text-center">
                    <div class="susc-estado-cel">
                      <span class="susc-badge" :class="badgeClass(s.estado)">
                        {{ labelEstado(s.estado) }}
                      </span>
                      <span v-if="s.cancelacion_programada" class="susc-cancel-prog-chip">
                        En proceso de cancelación
                      </span>
                    </div>
                  </td>
                  <td class="text-center text-xs opacity-7">{{ fmtFecha(s.periodo_inicio) }}</td>
                  <td class="text-center text-xs">
                    <span :class="{ 'text-warn': esPorVencer(s) }">{{ fmtFecha(s.periodo_fin) }}</span>
                  </td>
                  <td class="text-center text-xs">
                    <span v-if="s.gracia_hasta" class="text-warn">{{ fmtFecha(s.gracia_hasta) }}</span>
                    <span v-else class="opacity-4">—</span>
                  </td>
                  <td class="text-center">
                    <div class="acciones-cel">
                      <button
                        v-if="s.estado === 'gracia' || s.estado === 'vencida'"
                        class="btn-retry"
                        :disabled="reintentando === s.suscripcion_id"
                        @click="reintentar(s)"
                        title="Reintentar cobro en Stripe"
                      >
                        <span v-if="reintentando === s.suscripcion_id">...</span>
                        <span v-else>↺ Reintentar</span>
                      </button>
                      <button
                        class="btn-detalle"
                        @click="abrirDetalle(s)"
                        title="Ver detalle"
                      >
                        🔍
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-else class="text-sm opacity-6 mt-2">No hay suscripciones en este estado.</div>

          <!-- Paginación -->
          <div v-if="totalPages > 1" class="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-3">
            <p class="text-xs opacity-7 mb-0">{{ desde }}-{{ hasta }} de {{ listaFiltrada.length }}</p>
            <div class="btn-group">
              <button class="btn-pagi" :disabled="page <= 1" @click="page = 1">«</button>
              <button class="btn-pagi" :disabled="page <= 1" @click="page--">‹</button>
              <button class="btn-pagi" disabled>{{ page }} / {{ totalPages }}</button>
              <button class="btn-pagi" :disabled="page >= totalPages" @click="page++">›</button>
              <button class="btn-pagi" :disabled="page >= totalPages" @click="page = totalPages">»</button>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>

  <!-- MODAL DETALLE SUSCRIPCIÓN -->
  <Teleport to="body">
    <Transition name="susc-modal-fade">
      <div v-if="modalVisible" class="susc-modal-overlay" @click.self="cerrarModal">
        <div class="susc-modal-box">

          <!-- Cabecera -->
          <div class="susc-modal-header">
            <span class="susc-modal-title">Detalle de suscripción</span>
            <button class="susc-modal-close" @click="cerrarModal">×</button>
          </div>

          <div v-if="detalle" class="susc-modal-body">

            <!-- Badge estado -->
            <div class="susc-modal-estado">
              <span class="susc-badge" :class="badgeClass(detalle.estado)">
                {{ labelEstado(detalle.estado) }}
              </span>
              <span v-if="detalle.cancelacion_programada" class="susc-badge susc-badge--cancel-prog ms-2">
                Cancelación programada
              </span>
            </div>
            <div v-if="detalle.cancelacion_programada" class="susc-cancel-prog-aviso">
              La suscripción no se renovará al finalizar el período actual. El acceso se cortará automáticamente al vencer.
            </div>

            <!-- Grid de datos -->
            <div class="susc-modal-grid">
              <div class="susc-modal-field">
                <span class="susc-modal-label">Suscriptor</span>
                <span class="susc-modal-value">{{ detalle.nombre_usuario || '—' }}</span>
              </div>
              <div class="susc-modal-field">
                <span class="susc-modal-label">Email</span>
                <span class="susc-modal-value">{{ detalle.email }}</span>
              </div>
              <div class="susc-modal-field">
                <span class="susc-modal-label">Servicio</span>
                <span class="susc-modal-value">{{ detalle.producto }}</span>
              </div>
              <div class="susc-modal-field">
                <span class="susc-modal-label">Importe</span>
                <span class="susc-modal-value">{{ fmtImporte(detalle.importe, detalle.moneda) }}</span>
              </div>
              <div v-if="detalle.referencia" class="susc-modal-field">
                <span class="susc-modal-label">Referencia</span>
                <span class="susc-modal-value susc-mono">{{ detalle.referencia }}</span>
              </div>
              <div class="susc-modal-field">
                <span class="susc-modal-label">País</span>
                <span class="susc-modal-value">{{ paisNombre(detalle.pais_compra) }}</span>
              </div>
              <div class="susc-modal-field">
                <span class="susc-modal-label">Alta</span>
                <span class="susc-modal-value">{{ fmtFecha(detalle.periodo_inicio) }}</span>
              </div>
              <div class="susc-modal-field">
                <span class="susc-modal-label">Próx. renovación</span>
                <span class="susc-modal-value" :class="{ 'text-warn': esPorVencer(detalle) }">
                  {{ fmtFecha(detalle.periodo_fin) }}
                </span>
              </div>
              <div v-if="detalle.gracia_hasta" class="susc-modal-field susc-modal-field--gracia">
                <span class="susc-modal-label">
                  <span class="susc-gracia-icon">⚠️</span> Acceso hasta
                </span>
                <span class="susc-modal-value susc-gracia-fecha">{{ fmtFecha(detalle.gracia_hasta) }}</span>
                <span class="susc-gracia-hint">Cobro fallido — el acceso al canal expira en esta fecha</span>
              </div>
              <div v-if="detalle.invite_link" class="susc-modal-field susc-modal-field--full">
                <span class="susc-modal-label">Enlace de invitación</span>
                <a :href="detalle.invite_link" target="_blank" rel="noopener" class="susc-invite-link">
                  {{ detalle.invite_link }}
                </a>
              </div>
              <div v-if="detalle.telegram_id" class="susc-modal-field">
                <span class="susc-modal-label">Telegram ID</span>
                <span class="susc-modal-value susc-mono">{{ detalle.telegram_id }}</span>
              </div>
              <div v-if="detalle.telegram_nombre || detalle.telegram_username" class="susc-modal-field">
                <span class="susc-modal-label">Nick Telegram</span>
                <span class="susc-modal-value">
                  <span v-if="detalle.telegram_username" class="susc-mono">@{{ detalle.telegram_username }}</span>
                  <span v-else>{{ detalle.telegram_nombre }}</span>
                  <span v-if="detalle.telegram_username && detalle.telegram_nombre" class="susc-tg-nombre"> · {{ detalle.telegram_nombre }}</span>
                </span>
              </div>
            </div>

            <!-- Histórico de pagos (inline) -->
            <div v-if="detalle.pedido_id" class="susc-hist-zone">
              <p class="susc-cancel-titulo">Histórico de pagos</p>
              <div v-if="historialLoading" class="susc-hist-loading">
                <span class="spinner-border spinner-border-sm me-2"></span>Cargando…
              </div>
              <div v-else-if="historialEventos.length === 0" class="susc-hist-empty">Sin registros de pagos.</div>
              <div v-else class="susc-hist-timeline">
                <div v-for="(ev, idx) in historialEventos" :key="idx" class="susc-hist-item" :class="'susc-hist-' + ev.tipo">
                  <div class="susc-hist-dot"></div>
                  <div class="susc-hist-content">
                    <div class="susc-hist-label">{{ labelEvento(ev.tipo, idx) }}</div>
                    <div class="susc-hist-fecha">{{ fmtFecha(ev.fecha) }}</div>
                    <div v-if="ev.importe !== null && ev.importe > 0" class="susc-hist-importe">
                      {{ fmtImporte(ev.importe, ev.moneda) }}
                    </div>
                    <div v-else-if="ev.es_trial" class="susc-hist-importe susc-hist-importe--trial">Trial gratuito</div>
                    <div v-if="ev.tipo === 'pago_fallido' && ev.motivo" class="susc-hist-motivo">{{ ev.motivo }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Regenerar y reenviar enlace -->
            <div v-if="detalle.estado !== 'cancelada'" class="susc-reenviar-zone">
              <p class="susc-cancel-titulo">Regenerar acceso</p>
              <p class="susc-reenviar-desc">Revoca el enlace actual, genera uno nuevo y lo envía por email al suscriptor.</p>
              <button
                class="btn-reenviar"
                :disabled="reenviando"
                @click="reenviarEnlace(detalle)"
              >
                <span v-if="reenviando" class="spinner-border spinner-border-sm"></span>
                <span v-else>🔄 Regenerar y reenviar enlace</span>
              </button>
            </div>

            <!-- Actualizar nick Telegram -->
            <div v-if="detalle.telegram_id" class="susc-reenviar-zone">
              <p class="susc-cancel-titulo">Nick de Telegram</p>
              <p class="susc-reenviar-desc">Sincroniza el nombre y username actuales de este suscriptor desde Telegram.</p>
              <button
                class="btn-reenviar"
                :disabled="actualizandoNick"
                @click="actualizarNick(detalle)"
              >
                <span v-if="actualizandoNick" class="spinner-border spinner-border-sm"></span>
                <span v-else>📋 Actualizar nick Telegram</span>
              </button>
            </div>

            <!-- Verificar acceso al canal -->
            <div v-if="detalle.telegram_id && detalle.canal_enlace" class="susc-reenviar-zone">
              <p class="susc-cancel-titulo">Acceso al canal</p>
              <p class="susc-reenviar-desc">Consulta en tiempo real si el suscriptor sigue dentro del canal premium de Telegram.</p>
              <button
                class="btn-reenviar"
                :disabled="verificandoCanal"
                @click="verificarAccesoCanal(detalle)"
              >
                <span v-if="verificandoCanal" class="spinner-border spinner-border-sm"></span>
                <span v-else>🔍 Verificar acceso al canal</span>
              </button>
              <div v-if="accesoCanal" class="susc-canal-resultado" :class="accesoCanal.en_canal ? 'susc-canal-ok' : 'susc-canal-fuera'">
                <span>{{ accesoCanal.en_canal ? '✅' : '❌' }} {{ accesoCanal.mensaje }}</span>
                <span v-if="accesoCanal.canal_nombre" class="susc-canal-nombre"> · {{ accesoCanal.canal_nombre }}</span>
              </div>
              <div v-if="accesoCanal && accesoCanal.res === 'ERROR'" class="susc-canal-resultado susc-canal-error">
                ⚠️ {{ accesoCanal.mensaje }}
              </div>
            </div>

            <!-- Zona cancelación -->
            <div v-if="detalle.estado !== 'cancelada'" class="susc-cancel-zone">
              <p class="susc-cancel-titulo">Cancelar suscripción</p>

              <!-- Paso 1: elegir tipo -->
              <div v-if="cancelPaso === 0" class="susc-cancel-opciones">
                <button
                  class="btn-cancel-op btn-cancel-op--sin"
                  @click="cancelPaso = 1; cancelConDevolucion = false"
                >
                  <span class="bco-icono">🚫</span>
                  <div class="bco-texto">
                    <span class="bco-titulo">Sin devolución</span>
                    <span class="bco-desc">El acceso se mantiene hasta el final del período actual. No se cobra el siguiente ciclo.</span>
                  </div>
                </button>
                <button
                  class="btn-cancel-op btn-cancel-op--con"
                  @click="cancelPaso = 1; cancelConDevolucion = true"
                >
                  <span class="bco-icono">💸</span>
                  <div class="bco-texto">
                    <span class="bco-titulo">Con devolución</span>
                    <span class="bco-desc">Cancelación inmediata + reembolso del último pago. El usuario es expulsado del canal al instante.</span>
                  </div>
                </button>
              </div>

              <!-- Paso 2: confirmar -->
              <div v-if="cancelPaso === 1" class="susc-cancel-confirm">
                <div class="susc-confirm-msg" :class="cancelConDevolucion ? 'susc-confirm-msg--red' : 'susc-confirm-msg--amber'">
                  <span v-if="!cancelConDevolucion">
                    ¿Confirmas que deseas cancelar la suscripción de <strong>{{ detalle.email }}</strong>?
                    El usuario seguirá con acceso hasta el <strong>{{ fmtFecha(detalle.periodo_fin) }}</strong>.
                  </span>
                  <span v-else>
                    ¿Confirmas la cancelación <strong>inmediata con reembolso</strong> de la suscripción de <strong>{{ detalle.email }}</strong>?
                    El usuario será expulsado del canal ahora mismo.
                  </span>
                </div>
                <div class="susc-confirm-btns">
                  <button class="btn-q-secondary" @click="cancelPaso = 0" :disabled="cancelando">
                    ← Volver
                  </button>
                  <button
                    class="btn-confirm-cancel"
                    :class="cancelConDevolucion ? 'btn-confirm-cancel--red' : 'btn-confirm-cancel--amber'"
                    :disabled="cancelando"
                    @click="ejecutarCancelacion"
                  >
                    <span v-if="cancelando" class="spinner-border spinner-border-sm"></span>
                    <span v-else>{{ cancelConDevolucion ? 'Cancelar con devolución' : 'Cancelar sin devolución' }}</span>
                  </button>
                </div>
              </div>
            </div>

            <div v-else class="susc-ya-cancelada">
              Suscripción cancelada.
            </div>

          </div>
        </div>
      </div>
    </Transition>

  </Teleport>
</template>

<script>
import axios from "axios";
import showSwal from "@/mixins/showSwal.js";

const PER_PAGE = 10;

const NOMBRES_PAISES = {
  AD:'Andorra', AE:'Emiratos Árabes', AF:'Afganistán', AL:'Albania', AM:'Armenia',
  AO:'Angola', AR:'Argentina', AT:'Austria', AU:'Australia', AZ:'Azerbaiyán',
  BA:'Bosnia y Herzegovina', BB:'Barbados', BD:'Bangladés', BE:'Bélgica',
  BF:'Burkina Faso', BG:'Bulgaria', BH:'Baréin', BI:'Burundi', BJ:'Benín',
  BN:'Brunéi', BO:'Bolivia', BR:'Brasil', BS:'Bahamas', BT:'Bután',
  BW:'Botsuana', BY:'Bielorrusia', BZ:'Belice', CA:'Canadá', CD:'R. D. del Congo',
  CF:'Rep. Centroafricana', CG:'Congo', CH:'Suiza', CI:'Costa de Marfil',
  CL:'Chile', CM:'Camerún', CN:'China', CO:'Colombia', CR:'Costa Rica',
  CU:'Cuba', CV:'Cabo Verde', CY:'Chipre', CZ:'Chequia', DE:'Alemania',
  DJ:'Yibuti', DK:'Dinamarca', DM:'Dominica', DO:'Rep. Dominicana',
  DZ:'Argelia', EC:'Ecuador', EE:'Estonia', EG:'Egipto', ER:'Eritrea',
  ES:'España', ET:'Etiopía', FI:'Finlandia', FJ:'Fiyi', FR:'Francia',
  GA:'Gabón', GB:'Reino Unido', GD:'Granada', GE:'Georgia', GH:'Ghana',
  GM:'Gambia', GN:'Guinea', GQ:'Guinea Ecuatorial', GR:'Grecia',
  GT:'Guatemala', GW:'Guinea-Bisáu', GY:'Guyana', HN:'Honduras',
  HR:'Croacia', HT:'Haití', HU:'Hungría', ID:'Indonesia', IE:'Irlanda',
  IL:'Israel', IN:'India', IQ:'Irak', IR:'Irán', IS:'Islandia', IT:'Italia',
  JM:'Jamaica', JO:'Jordania', JP:'Japón', KE:'Kenia', KG:'Kirguistán',
  KH:'Camboya', KM:'Comoras', KP:'Corea del Norte', KR:'Corea del Sur',
  KW:'Kuwait', KZ:'Kazajistán', LA:'Laos', LB:'Líbano', LC:'Santa Lucía',
  LI:'Liechtenstein', LK:'Sri Lanka', LR:'Liberia', LS:'Lesoto',
  LT:'Lituania', LU:'Luxemburgo', LV:'Letonia', LY:'Libia', MA:'Marruecos',
  MC:'Mónaco', MD:'Moldavia', ME:'Montenegro', MG:'Madagascar',
  MK:'Macedonia del Norte', ML:'Malí', MM:'Myanmar', MN:'Mongolia',
  MR:'Mauritania', MT:'Malta', MU:'Mauricio', MV:'Maldivas', MW:'Malaui',
  MX:'México', MY:'Malasia', MZ:'Mozambique', NA:'Namibia', NE:'Níger',
  NG:'Nigeria', NI:'Nicaragua', NL:'Países Bajos', NO:'Noruega', NP:'Nepal',
  NZ:'Nueva Zelanda', OM:'Omán', PA:'Panamá', PE:'Perú', PH:'Filipinas',
  PK:'Pakistán', PL:'Polonia', PT:'Portugal', PY:'Paraguay', QA:'Catar',
  RO:'Rumania', RS:'Serbia', RU:'Rusia', RW:'Ruanda', SA:'Arabia Saudita',
  SC:'Seychelles', SD:'Sudán', SE:'Suecia', SG:'Singapur', SI:'Eslovenia',
  SK:'Eslovaquia', SL:'Sierra Leona', SM:'San Marino', SN:'Senegal',
  SO:'Somalia', SR:'Surinam', SS:'Sudán del Sur', SV:'El Salvador',
  SY:'Siria', SZ:'Suazilandia', TD:'Chad', TG:'Togo', TH:'Tailandia',
  TJ:'Tayikistán', TL:'Timor Oriental', TM:'Turkmenistán', TN:'Túnez',
  TO:'Tonga', TR:'Turquía', TT:'Trinidad y Tobago', TZ:'Tanzania',
  UA:'Ucrania', UG:'Uganda', US:'Estados Unidos', UY:'Uruguay',
  UZ:'Uzbekistán', VC:'San Vicente y las Granadinas', VE:'Venezuela',
  VN:'Vietnam', VU:'Vanuatu', WS:'Samoa', YE:'Yemen',
  ZA:'Sudáfrica', ZM:'Zambia', ZW:'Zimbabue',
};

export default {
  name: "PanelTipsterSuscripciones",
  mixins: [showSwal],
  props: {
    datos: {
      type: Object,
      default: () => ({
        activas: 0, en_gracia: 0, vencidas: 0, canceladas: 0, por_vencer_7d: 0,
        altas_periodo: 0, filtro_periodo: false, lista: [],
      }),
    },
    tipsterId: { type: [Number, String], default: null },
  },
  data() {
    return {
      page: 1,
      sortCol: '',
      sortDir: 'asc',
      filtroEstado: '',
      filtroProducto: '',
      filtroTexto: '',
      reintentando: null,
      reenviando: false,
      // Modal
      modalVisible: false,
      detalle: null,
      cancelPaso: 0,
      cancelConDevolucion: false,
      cancelando: false,
      actualizandoNick: false,
      verificandoCanal: false,
      accesoCanal: null,
      historialLoading: false,
      historialEventos: [],
    };
  },
  computed: {
    tieneData() {
      return (this.datos.activas + this.datos.en_gracia + this.datos.vencidas + this.datos.por_vencer_7d + this.datos.canceladas) > 0;
    },
    lista() {
      return Array.isArray(this.datos.lista) ? this.datos.lista : [];
    },
    listaSorted() {
      if (!this.sortCol) return this.lista;
      const dir = this.sortDir === 'asc' ? 1 : -1;
      const col = this.sortCol;
      return [...this.lista].sort((a, b) => {
        if (col === 'periodo_inicio' || col === 'periodo_fin' || col === 'gracia_hasta') {
          const va = a[col] ? new Date(a[col]).getTime() : (dir === 1 ? Infinity : -Infinity);
          const vb = b[col] ? new Date(b[col]).getTime() : (dir === 1 ? Infinity : -Infinity);
          return (va - vb) * dir;
        }
        const va = String(a[col] ?? '').toLowerCase();
        const vb = String(b[col] ?? '').toLowerCase();
        return va < vb ? -dir : va > vb ? dir : 0;
      });
    },
    productosActivos() {
      const map = new Map();
      for (const s of this.lista) {
        if (!map.has(s.producto)) map.set(s.producto, { activas: 0, total: 0 });
        const p = map.get(s.producto);
        p.total++;
        if (s.estado === 'activa') p.activas++;
      }
      return Array.from(map.entries())
        .map(([nombre, c]) => ({ nombre, ...c }))
        .sort((a, b) => b.activas - a.activas);
    },
    listaTextual() {
      const txt = (this.filtroTexto || '').toLowerCase().trim();
      if (!txt) return this.listaSorted;
      return this.listaSorted.filter(s =>
        (s.email             || '').toLowerCase().includes(txt) ||
        (s.producto          || '').toLowerCase().includes(txt) ||
        (s.nombre_usuario    || '').toLowerCase().includes(txt) ||
        (s.telegram_username || '').toLowerCase().includes(txt) ||
        (s.telegram_nombre   || '').toLowerCase().includes(txt) ||
        (s.referencia        || '').toLowerCase().includes(txt) ||
        String(s.telegram_id || '').includes(txt)
      );
    },
    listaProducto() {
      if (!this.filtroProducto) return this.listaTextual;
      return this.listaTextual.filter(s => s.producto === this.filtroProducto);
    },
    listaFiltrada() {
      if (!this.filtroEstado) return this.listaProducto;
      if (this.filtroEstado === 'por_vencer') {
        return this.listaProducto.filter(s => s.estado === 'activa' && this.esPorVencer(s));
      }
      return this.listaProducto.filter(s => s.estado === this.filtroEstado);
    },
    totalPages() { return Math.max(1, Math.ceil(this.listaFiltrada.length / PER_PAGE)); },
    listaVisible() {
      const s = (this.page - 1) * PER_PAGE;
      return this.listaFiltrada.slice(s, s + PER_PAGE);
    },
    desde() { return this.listaFiltrada.length === 0 ? 0 : (this.page - 1) * PER_PAGE + 1; },
    hasta() { return Math.min(this.page * PER_PAGE, this.listaFiltrada.length); },
  },
  watch: {
    filtroTexto()    { this.page = 1; },
    filtroProducto() { this.page = 1; },
  },
  methods: {
    fmtImporte(importe, moneda) {
      if (!importe) return '—';
      const cur = (moneda || 'EUR').toUpperCase();
      const locale = cur === 'USD' ? 'en-US' : 'es-ES';
      return Number(importe).toLocaleString(locale, { style: 'currency', currency: cur });
    },
    paisNombre(code) {
      if (!code) return '—';
      const cc = String(code).trim().toUpperCase();
      return NOMBRES_PAISES[cc] || cc;
    },
    setFiltroProducto(nombre) {
      this.filtroProducto = this.filtroProducto === nombre ? '' : nombre;
    },
    setFiltro(estado) {
      const activando = this.filtroEstado !== estado;
      this.filtroEstado = activando ? estado : '';
      this.page = 1;
      if (activando && estado === 'vencida') {
        this.sortCol = 'periodo_fin';
        this.sortDir = 'desc';
      }
    },
    ordenar(col) {
      if (this.sortCol === col) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortCol = col;
        this.sortDir = 'asc';
      }
      this.page = 1;
    },
    badgeClass(estado) {
      return {
        activa:    'susc-badge--ok',
        gracia:    'susc-badge--gracia',
        vencida:   'susc-badge--err',
        cancelada: 'susc-badge--muted',
      }[estado] || 'susc-badge--muted';
    },
    labelEstado(estado) {
      return { activa: 'Activa', gracia: 'En gracia', vencida: 'Vencida', cancelada: 'Cancelada' }[estado] || estado;
    },
    fmtFecha(f) {
      if (!f) return '—';
      return new Date(f).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    esPorVencer(s) {
      if (s.estado !== 'activa' || !s.periodo_fin) return false;
      const diff = new Date(s.periodo_fin) - new Date();
      return diff > 0 && diff < 7 * 86400000;
    },
    abrirDetalle(s) {
      this.detalle = s;
      this.cancelPaso = 0;
      this.cancelConDevolucion = false;
      this.cancelando = false;
      this.accesoCanal = null;
      this.historialEventos = [];
      this.modalVisible = true;
      if (s.pedido_id) this.cargarHistorial(s.pedido_id);
    },
    cerrarModal() {
      if (this.cancelando && this.cancelPaso === 1) return;
      this.modalVisible = false;
      this.detalle = null;
      this.cancelando = false;
    },
    async cargarHistorial(pedidoId) {
      this.historialLoading = true;
      this.historialEventos = [];
      try {
        const token = JSON.parse(localStorage.getItem("userF"));
        const res = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/api/v2/tipster/panel/${this.tipsterId}/pedido/${pedidoId}/historial`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        this.historialEventos = res.data?.eventos || [];
      } catch {
        this.historialEventos = [];
      } finally {
        this.historialLoading = false;
      }
    },
    labelEvento(tipo, idx) {
      if (tipo === 'pago_ok' || tipo === 'pago_recuperado') {
        const previos = this.historialEventos.slice(0, idx).filter(e => e.tipo === 'pago_ok' || e.tipo === 'pago_recuperado').length;
        if (tipo === 'pago_recuperado') return 'Pago recuperado';
        return previos === 0 ? 'Primer pago' : 'Renovación';
      }
      return { creada: 'Alta', pago_fallido: 'Pago fallido', cancelada: 'Cancelada', vencida: 'Vencida', cancelacion_programada: 'Cancelación programada', entra_gracia: 'Entró en gracia', pago_recuperado: 'Pago recuperado', reactivada: 'Reactivada' }[tipo] || tipo;
    },
    async reintentar(s) {
      if (!this.tipsterId) return;
      this.reintentando = s.suscripcion_id;
      try {
        const token = JSON.parse(localStorage.getItem("userF"));
        const res = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/api/v2/tipster/panel/${this.tipsterId}/reintentar-pago`,
          { suscripcion_id: s.suscripcion_id },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.data?.res === 'OK') {
          this.showSwal({ type: "success", message: "Reintento lanzado correctamente. Stripe procesará el cobro." });
        } else {
          this.showSwal({ type: "error", message: res.data?.mensaje || "Error al reintentar el pago." });
        }
      } catch (e) {
        this.showSwal({ type: "error", message: e?.response?.data?.mensaje || "Error de red al reintentar el pago." });
      } finally {
        this.reintentando = null;
      }
    },
    async reenviarEnlace(s) {
      if (!this.tipsterId || this.reenviando) return;
      this.reenviando = true;
      try {
        const token = JSON.parse(localStorage.getItem("userF"));
        const res = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/api/v2/tipster/panel/${this.tipsterId}/reenviar-enlace`,
          { suscripcion_id: s.suscripcion_id },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.data?.res === 'OK') {
          // Actualizar el enlace visible en el modal
          if (res.data.invite_link && this.detalle) {
            this.detalle = { ...this.detalle, invite_link: res.data.invite_link };
            // Sincronizar en la lista
            const idx = this.lista.findIndex(x => x.suscripcion_id === s.suscripcion_id);
            if (idx !== -1) this.lista[idx] = { ...this.lista[idx], invite_link: res.data.invite_link };
          }
          this.showSwal({ type: "success", message: res.data.mensaje });
        } else {
          this.showSwal({ type: "error", message: res.data?.mensaje || "Error al regenerar el enlace." });
        }
      } catch (e) {
        this.showSwal({ type: "error", message: e?.response?.data?.mensaje || "Error al regenerar el enlace." });
      } finally {
        this.reenviando = false;
      }
    },
    async verificarAccesoCanal(s) {
      if (!this.tipsterId || this.verificandoCanal) return;
      this.verificandoCanal = true;
      this.accesoCanal = null;
      try {
        const token = JSON.parse(localStorage.getItem("userF"));
        const res = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/api/v2/tipster/panel/${this.tipsterId}/verificar-acceso-canal`,
          { suscripcion_id: s.suscripcion_id },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        this.accesoCanal = res.data;
      } catch (e) {
        this.accesoCanal = { res: 'ERROR', mensaje: e?.response?.data?.mensaje || 'Error al consultar Telegram.' };
      } finally {
        this.verificandoCanal = false;
      }
    },
    async actualizarNick(s) {
      if (!this.tipsterId || this.actualizandoNick) return;
      this.actualizandoNick = true;
      try {
        const token = JSON.parse(localStorage.getItem("userF"));
        const res = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/api/v2/tipster/panel/${this.tipsterId}/actualizar-nicks`,
          { suscripcion_id: s.suscripcion_id },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.data?.res === 'OK') {
          this.showSwal({ type: "success", message: res.data.mensaje });
        } else {
          this.showSwal({ type: "error", message: res.data?.mensaje || "Error al encolar la actualización." });
        }
      } catch (e) {
        this.showSwal({ type: "error", message: e?.response?.data?.mensaje || "Error al actualizar el nick." });
      } finally {
        this.actualizandoNick = false;
      }
    },
    async ejecutarCancelacion() {
      if (!this.detalle || !this.tipsterId || this.cancelando) return;
      this.cancelando = true;
      try {
        const token = JSON.parse(localStorage.getItem("userF"));
        const res = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/api/v2/tipster/panel/${this.tipsterId}/cancelar-suscripcion`,
          {
            suscripcion_id: this.detalle.suscripcion_id,
            con_devolucion: this.cancelConDevolucion,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.data?.res === 'OK') {
          this.showSwal({ type: "success", message: res.data.mensaje });
          // Actualizar estado en la lista local
          const idx = this.lista.findIndex(s => s.suscripcion_id === this.detalle.suscripcion_id);
          if (idx !== -1) {
            if (this.cancelConDevolucion) {
              this.lista[idx] = { ...this.lista[idx], estado: 'cancelada' };
            } else {
              this.lista[idx] = { ...this.lista[idx], cancelacion_programada: true };
            }
          }
          // cancelPaso = 0 para que cerrarModal() no quede bloqueado por el guard
          this.cancelPaso = 0;
          this.cerrarModal();
          this.$emit('recargar');
        } else {
          this.showSwal({ type: "error", message: res.data?.mensaje || "Error al cancelar." });
          this.cancelPaso = 0;
          this.cancelando = false;
        }
      } catch (e) {
        this.showSwal({ type: "error", message: e?.response?.data?.mensaje || "Error al cancelar la suscripción." });
        this.cancelPaso = 0;
        this.cancelando = false;
      }
    },
  },
};
</script>

<style scoped>
/* KPI cards */
.susc-kpi {
  border: 1px solid var(--q-border);
  background: var(--q-card-alt);
  border-radius: 12px;
  padding: 12px 14px;
  min-height: 80px;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  text-align: center; gap: 4px;
  cursor: pointer;
  transition: transform .15s, box-shadow .15s;
  user-select: none;
}
.susc-kpi:hover { transform: translateY(-2px); box-shadow: 0 4px 14px rgba(0,0,0,.12); }
.susc-kpi__k { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .4px; color: var(--q-muted); }
.susc-kpi__v { font-size: 22px; font-weight: 800; font-family: var(--q-font-mono); }
.susc-kpi__desc { font-size: 10px; color: var(--q-muted); margin-top: 2px; line-height: 1.3; }
.susc-kpi--ok    .susc-kpi__v { color: var(--q-green); }
.susc-kpi--warn  .susc-kpi__v { color: var(--q-amber); }
.susc-kpi--gracia .susc-kpi__v { color: var(--q-amber); }
.susc-kpi--err   .susc-kpi__v { color: var(--q-red); }
.susc-kpi--muted .susc-kpi__v { color: var(--q-muted); }
.susc-kpi--active { box-shadow: 0 0 0 2px var(--q-gold) !important; }

/* Chips de productos */
.susc-productos {
  display: flex; flex-wrap: wrap; gap: 8px;
}
.susc-prod-chip {
  display: flex; align-items: center; gap: 6px;
  background: var(--q-card-alt);
  border: 1px solid var(--q-border);
  border-radius: 20px;
  padding: 5px 12px 5px 14px;
  cursor: pointer;
  transition: border-color .15s, background .15s;
  user-select: none;
}
.susc-prod-chip:hover { border-color: var(--q-gold); }
.susc-prod-chip--active {
  border-color: var(--q-gold) !important;
  background: var(--q-hover);
  box-shadow: 0 0 0 2px var(--q-gold);
}
.susc-prod-nombre { font-size: 12px; font-weight: 600; color: var(--q-text); }
.susc-prod-count {
  min-width: 20px; height: 20px;
  display: flex; align-items: center; justify-content: center;
  background: var(--q-green-soft);
  color: var(--q-green);
  border-radius: 10px;
  font-size: 11px; font-weight: 800; font-family: var(--q-font-mono);
  padding: 0 5px;
}
.susc-prod-chip--active .susc-prod-count {
  background: var(--q-gold);
  color: #1a1000;
}

/* Altas del periodo */
.susc-altas-badge {
  display: flex; align-items: baseline; gap: 8px;
  background: var(--q-hover); border: 1px solid var(--q-border-md);
  border-radius: 10px; padding: 6px 14px;
}
.susc-altas-label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .4px; color: var(--q-muted); }
.susc-altas-valor { font-size: 20px; font-weight: 800; font-family: var(--q-font-mono); color: var(--q-gold); }

/* Filtro activo badge */
.susc-filtro-activo { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.susc-filtro-badge {
  font-size: 12px; color: var(--q-muted);
  background: var(--q-hover); border: 1px solid var(--q-border-md);
  border-radius: 20px; padding: 4px 12px;
}
.susc-filtro-clear {
  background: none; border: none; color: var(--q-gold);
  font-size: 12px; font-weight: 600; cursor: pointer; padding: 0;
}
.susc-filtro-clear:hover { text-decoration: underline; }

/* Badges estado */
.susc-badge {
  display: inline-block; padding: 3px 10px; border-radius: 20px;
  font-size: 11px; font-weight: 700; white-space: nowrap;
}
.susc-badge--ok    { background: var(--q-green-soft); color: var(--q-green);  border: 1px solid var(--q-green); }
.susc-badge--gracia{ background: var(--q-amber-soft); color: var(--q-amber);  border: 1px solid var(--q-amber); }
.susc-badge--err   { background: var(--q-red-soft);   color: var(--q-red);    border: 1px solid var(--q-red); }
.susc-badge--muted { background: var(--q-hover);      color: var(--q-muted);  border: 1px solid var(--q-border-md); }
.susc-badge--cancel-prog { background: rgba(239,68,68,.1); color: #f87171; border: 1px solid rgba(239,68,68,.35); font-size: 0.68rem; }
.susc-estado-cel { display: flex; flex-direction: column; align-items: center; gap: 4px; }
.susc-cancel-prog-chip { font-size: 0.68rem; color: #f87171; white-space: nowrap; }
.susc-cancel-prog-aviso {
  margin: 8px 0 4px;
  padding: 8px 12px;
  background: rgba(239,68,68,.08);
  border: 1px solid rgba(239,68,68,.25);
  border-radius: 8px;
  color: #f87171;
  font-size: 0.78rem;
  line-height: 1.4;
}

/* Texto "por vencer" */
.text-warn { color: var(--q-amber) !important; font-weight: 600; }

/* Acciones en celda */
.acciones-cel { display: flex; align-items: center; justify-content: center; gap: 6px; flex-wrap: wrap; }

/* Botón reintentar */
.btn-retry {
  background: var(--q-amber-soft);
  color: var(--q-amber);
  border: 1px solid var(--q-amber);
  border-radius: 8px;
  padding: 5px 12px;
  font-size: 12px; font-weight: 700; cursor: pointer; white-space: nowrap;
  transition: .15s;
}
.btn-retry:hover:not(:disabled) { background: var(--q-amber); color: #1a1000; }
.btn-retry:disabled { opacity: .5; cursor: not-allowed; }

/* Botón detalle */
.btn-detalle {
  background: var(--q-card-alt);
  border: 1px solid var(--q-border-md);
  border-radius: 8px;
  padding: 5px 10px;
  font-size: 13px; cursor: pointer; transition: .15s;
}
.btn-detalle:hover { border-color: var(--q-gold); background: var(--q-hover); }

/* Paginación */
.btn-pagi {
  background: var(--q-card-alt); border: 1px solid var(--q-border-md);
  color: var(--q-muted); padding: 4px 10px; font-size: 0.8rem; transition: .15s;
}
.btn-pagi:hover:not(:disabled) { background: var(--q-hover); color: var(--q-gold); border-color: var(--q-gold); }
.btn-pagi:disabled { opacity: .4; cursor: not-allowed; }

/* Tabla */
.email-cell { font-size: 12px; max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.table-empirulada { border-collapse: separate !important; border-spacing: 0 8px; }
.table-empirulada thead th { border: 0 !important; background: var(--q-hover); color: var(--q-text); font-weight: 700; padding: 10px 14px; }
.table-empirulada tbody tr { background: var(--q-card); }
.table-empirulada tbody td { border: 0 !important; padding: 10px 14px; vertical-align: middle; }
.table-empirulada > :not(caption) > * > * { border-bottom-width: 0 !important; }
.table-empirulada tbody tr td:first-child { border-top-left-radius: 10px; border-bottom-left-radius: 10px; }
.table-empirulada tbody tr td:last-child  { border-top-right-radius: 10px; border-bottom-right-radius: 10px; }
.table-empirulada thead, .table-empirulada thead tr, .table-empirulada thead th { border-radius: 0 !important; }
.table-empirulada thead tr { box-shadow: none !important; background: transparent !important; }

/* ===== MODAL ===== */
.susc-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999999;
  background: rgba(0,0,0,0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.susc-modal-box {
  background: var(--q-card);
  border: 1px solid var(--q-border);
  border-radius: 16px;
  width: 100%;
  max-width: 540px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0,0,0,0.5);
}
.susc-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px 14px;
  border-bottom: 1px solid var(--q-border);
}
.susc-modal-title {
  font-size: 1rem; font-weight: 700; color: var(--q-text);
}
.susc-modal-close {
  background: none; border: none; color: var(--q-muted);
  font-size: 1.5rem; line-height: 1; cursor: pointer; padding: 0 4px;
  transition: color .15s;
}
.susc-modal-close:hover { color: var(--q-text); }
.susc-modal-body { padding: 18px 20px 22px; }
.susc-modal-estado { margin-bottom: 16px; }

/* Grid de campos */
.susc-modal-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 16px;
  margin-bottom: 20px;
}
.susc-modal-field { display: flex; flex-direction: column; gap: 3px; }
.susc-modal-field--full { grid-column: 1 / -1; }
.susc-modal-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; color: var(--q-muted); }
.susc-modal-value { font-size: 0.9rem; color: var(--q-text); font-weight: 500; word-break: break-all; }
.susc-modal-field--gracia { grid-column: 1 / -1; background: rgba(245,158,11,.08); border: 1px solid rgba(245,158,11,.25); border-radius: 8px; padding: 10px 12px; gap: 4px; }
.susc-gracia-icon { font-size: 0.85rem; }
.susc-gracia-fecha { color: #f59e0b !important; font-size: 1rem !important; }
.susc-gracia-hint { font-size: 0.72rem; color: #f59e0b; opacity: .8; margin-top: 2px; }
.susc-tg-nombre { font-size: 0.8rem; color: var(--q-muted); font-weight: 400; }
.susc-mono { font-family: var(--q-font-mono); font-size: 0.85rem; }
.susc-invite-link { font-size: 0.8rem; color: var(--q-gold); word-break: break-all; }

/* Zona reenviar enlace */
.susc-reenviar-zone {
  border-top: 1px solid var(--q-border);
  padding-top: 18px;
  margin-top: 4px;
}
.susc-reenviar-desc {
  font-size: 0.82rem;
  color: var(--q-muted);
  margin-bottom: 10px;
}
.btn-reenviar {
  background: var(--q-card-alt);
  border: 1px solid var(--q-border-md);
  color: var(--q-text);
  border-radius: 8px;
  padding: 8px 18px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: .15s;
  display: flex;
  align-items: center;
  gap: 6px;
}
.btn-reenviar:hover:not(:disabled) { border-color: var(--q-gold); color: var(--q-gold); }
.btn-reenviar:disabled { opacity: .5; cursor: not-allowed; }

.susc-canal-resultado {
  margin-top: 10px;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.85rem;
  line-height: 1.4;
}
.susc-canal-ok { background: rgba(34,197,94,0.12); color: #22c55e; border: 1px solid rgba(34,197,94,0.3); }
.susc-canal-fuera { background: rgba(239,68,68,0.12); color: #ef4444; border: 1px solid rgba(239,68,68,0.3); }
.susc-canal-error { background: rgba(234,179,8,0.12); color: #eab308; border: 1px solid rgba(234,179,8,0.3); }
.susc-canal-nombre { opacity: 0.75; }

/* Zona cancelación */
.susc-cancel-zone {
  border-top: 1px solid var(--q-border);
  padding-top: 18px;
  margin-top: 4px;
}
.susc-cancel-titulo {
  font-size: 0.8rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: .5px; color: var(--q-muted); margin-bottom: 12px;
}
.susc-cancel-opciones { display: flex; flex-direction: column; gap: 10px; }
.btn-cancel-op {
  display: flex; align-items: flex-start; gap: 12px;
  background: var(--q-card-alt); border: 1px solid var(--q-border-md);
  border-radius: 10px; padding: 12px 14px; cursor: pointer;
  text-align: left; transition: .15s; width: 100%;
}
.btn-cancel-op--sin:hover { border-color: var(--q-amber); background: var(--q-amber-soft); }
.btn-cancel-op--con:hover { border-color: var(--q-red); background: var(--q-red-soft); }
.bco-icono { font-size: 1.3rem; flex-shrink: 0; }
.bco-texto { display: flex; flex-direction: column; gap: 2px; }
.bco-titulo { font-size: 0.9rem; font-weight: 700; color: var(--q-text); }
.bco-desc { font-size: 0.78rem; color: var(--q-muted); line-height: 1.4; }

/* Confirmación */
.susc-cancel-confirm { display: flex; flex-direction: column; gap: 14px; }
.susc-confirm-msg {
  padding: 12px 14px; border-radius: 8px;
  font-size: 0.85rem; line-height: 1.5; color: var(--q-text);
}
.susc-confirm-msg--amber { background: var(--q-amber-soft); border: 1px solid var(--q-amber); }
.susc-confirm-msg--red   { background: var(--q-red-soft);   border: 1px solid var(--q-red); }
.susc-confirm-btns { display: flex; gap: 10px; justify-content: flex-end; }
.btn-q-secondary {
  background: var(--q-card-alt); border: 1px solid var(--q-border-md);
  color: var(--q-muted); border-radius: 8px; padding: 8px 18px;
  font-size: 0.85rem; font-weight: 600; cursor: pointer; transition: .15s;
}
.btn-q-secondary:hover:not(:disabled) { color: var(--q-text); border-color: var(--q-border-md); }
.btn-q-secondary:disabled { opacity: .5; cursor: not-allowed; }
.btn-confirm-cancel {
  border: none; border-radius: 8px; padding: 8px 20px;
  font-size: 0.85rem; font-weight: 700; cursor: pointer; transition: .15s;
  display: flex; align-items: center; gap: 6px;
}
.btn-confirm-cancel--amber { background: var(--q-amber); color: #1a1000; }
.btn-confirm-cancel--amber:hover:not(:disabled) { opacity: .85; }
.btn-confirm-cancel--red   { background: var(--q-red);   color: #fff; }
.btn-confirm-cancel--red:hover:not(:disabled)   { opacity: .85; }
.btn-confirm-cancel:disabled { opacity: .5; cursor: not-allowed; }

.susc-ya-cancelada {
  padding: 10px 14px; border-radius: 8px; text-align: center;
  background: var(--q-hover); color: var(--q-muted); font-size: 0.85rem;
  border-top: 1px solid var(--q-border); margin-top: 4px; padding-top: 18px;
}

/* Animación modal */
.susc-modal-fade-enter-active,
.susc-modal-fade-leave-active { transition: opacity .2s ease; }
.susc-modal-fade-enter-from,
.susc-modal-fade-leave-to    { opacity: 0; }

@media (max-width: 480px) {
  .susc-modal-grid { grid-template-columns: 1fr; }
  .susc-modal-field--full { grid-column: 1; }
  .susc-confirm-btns { flex-direction: column; }
}

/* Historial inline */
.susc-hist-zone {
  border-top: 1px solid var(--q-border);
  padding-top: 18px;
  margin-top: 4px;
}
.susc-hist-loading, .susc-hist-empty {
  display: flex; align-items: center;
  font-size: 0.85rem; color: var(--q-muted); font-style: italic; padding: 8px 0;
}
.susc-hist-timeline {
  display: flex; flex-direction: column; gap: 0;
  position: relative; padding-left: 24px;
}
.susc-hist-timeline::before {
  content: ''; position: absolute; left: 7px; top: 10px; bottom: 10px;
  width: 2px; background: var(--q-border);
}
.susc-hist-item {
  display: flex; align-items: flex-start; gap: 14px;
  padding: 10px 0; position: relative;
}
.susc-hist-dot {
  width: 14px; height: 14px; border-radius: 50%;
  border: 2px solid var(--q-border-md); background: var(--q-card);
  flex-shrink: 0; margin-left: -24px; margin-top: 2px;
  z-index: 1; position: relative;
}
.susc-hist-pago_ok .susc-hist-dot,
.susc-hist-pago_recuperado .susc-hist-dot { border-color: var(--q-green); background: var(--q-green-soft); }
.susc-hist-pago_fallido .susc-hist-dot    { border-color: var(--q-red, #ef4444); background: rgba(239,68,68,.1); }
.susc-hist-cancelada .susc-hist-dot,
.susc-hist-vencida .susc-hist-dot         { border-color: var(--q-muted); background: var(--q-card-alt); }
.susc-hist-creada .susc-hist-dot          { border-color: #8b5cf6; background: rgba(139,92,246,.12); }
.susc-hist-content { flex: 1; }
.susc-hist-label   { font-size: 0.85rem; font-weight: 700; color: var(--q-text); }
.susc-hist-pago_ok .susc-hist-label,
.susc-hist-pago_recuperado .susc-hist-label { color: var(--q-green); }
.susc-hist-pago_fallido .susc-hist-label    { color: var(--q-red, #ef4444); }
.susc-hist-cancelada .susc-hist-label,
.susc-hist-vencida .susc-hist-label         { color: var(--q-muted); }
.susc-hist-creada .susc-hist-label          { color: #a78bfa; }
.susc-hist-fecha   { font-size: 0.75rem; color: var(--q-muted); margin-top: 1px; }
.susc-hist-importe {
  font-size: 0.82rem; font-weight: 700;
  font-family: var(--q-font-mono); color: var(--q-green); margin-top: 2px;
}
.susc-hist-importe--trial { color: #a78bfa; font-family: inherit; font-weight: 600; }
.susc-hist-motivo { font-size: 0.75rem; color: var(--q-red, #ef4444); opacity: .85; margin-top: 2px; }

/* Toolbar buscador */
.susc-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.input-buscar {
  flex: 1 1 220px;
  max-width: 360px;
  height: 36px;
  background: var(--q-card-alt);
  border: 1px solid var(--q-border);
  border-radius: 8px;
  padding: 0 12px;
  font-size: 0.85rem;
  color: var(--q-text);
}
.input-buscar:focus {
  outline: none;
  border-color: var(--q-gold);
}
/* Ajuste: filtro activo dentro del toolbar ya no necesita margen propio */
.susc-toolbar .susc-filtro-activo { margin-bottom: 0; }
</style>
